import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { X, ShieldCheck, CalendarClock, MapPin, Plus, Minus, Repeat } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api/client";

const inr = (n) => `₹${Number(n || 0).toFixed(2)}`;

/**
 * SubscribeDialog
 * Props:
 *  - open: boolean
 *  - product: { id, name, brand, pack_size, selling_price, mrp, prescription_required, images[] }
 *  - onClose()
 *  - onCreated(sub): callback after success
 */
const SubscribeDialog = ({ open, product, onClose, onCreated }) => {
    const [qty, setQty] = useState(1);
    const [addresses, setAddresses] = useState([]);
    const [selectedAddr, setSelectedAddr] = useState(null);
    const [prescriptions, setPrescriptions] = useState([]);
    const [selectedRx, setSelectedRx] = useState(null);
    const [loadingMeta, setLoadingMeta] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        setQty(1);
        const load = async () => {
            setLoadingMeta(true);
            try {
                const reqs = [api.get("/addresses")];
                if (product?.prescription_required) reqs.push(api.get("/prescriptions"));
                const results = await Promise.all(reqs);
                const addrs = results[0].data || [];
                setAddresses(addrs);
                const def = addrs.find((a) => a.is_default) || addrs[0];
                setSelectedAddr(def?.id || null);
                if (product?.prescription_required) {
                    const rxList = (results[1].data || []).filter((rx) => rx.ocr_status === "extracted");
                    setPrescriptions(rxList);
                    setSelectedRx(rxList[0]?.id || null);
                } else {
                    setPrescriptions([]);
                    setSelectedRx(null);
                }
            } catch (e) {
                toast.error("Could not load your account info");
            } finally {
                setLoadingMeta(false);
            }
        };
        load();
    }, [open, product]);

    const total = useMemo(() => (product?.selling_price || 0) * qty, [product, qty]);

    if (!open || !product) return null;

    const submit = async () => {
        if (submitting) return;
        if (!selectedAddr) {
            toast.error("Please add a delivery address first");
            return;
        }
        if (product.prescription_required && !selectedRx) {
            toast.error("Attach a verified prescription to subscribe");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/subscriptions", {
                product_id: product.id,
                qty,
                shipping_address_id: selectedAddr,
                prescription_id: selectedRx || undefined,
            });
            toast.success("Subscribed — refill reminders activated");
            onCreated?.(data);
            onClose?.();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : detail?.message || "Could not subscribe";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-zinc-900/40 backdrop-blur-sm sm:items-center" data-testid="subscribe-dialog">
            <div
                className="w-full max-w-lg rounded-t-3xl border border-zinc-100 bg-white p-6 shadow-2xl sm:rounded-3xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <p className="label-uppercase">Monthly subscription</p>
                        <h2 className="mt-1 text-xl font-semibold tracking-tight text-zinc-900">Set up auto-refill</h2>
                        <p className="mt-1 text-sm text-zinc-500">We'll remind you every 30 days to refill.</p>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="rounded-full p-2 text-zinc-500 hover:bg-zinc-100"
                        data-testid="subscribe-close-btn"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="mt-5 flex items-center gap-4 rounded-2xl border border-zinc-100 bg-zinc-50/60 p-4">
                    <img
                        src={product.images?.[0] || "/pills-logo.jpg"}
                        alt={product.name}
                        className="h-16 w-16 rounded-xl object-cover ring-1 ring-zinc-200"
                    />
                    <div className="min-w-0 flex-1">
                        <p className="line-clamp-1 text-sm font-semibold text-zinc-900">{product.name}</p>
                        <p className="text-xs text-zinc-500">{product.brand}{product.pack_size ? ` • ${product.pack_size}` : ""}</p>
                        <p className="mt-1 text-xs text-brand-700"><CalendarClock className="mr-1 inline h-3 w-3" /> Refills monthly</p>
                    </div>
                    <p className="text-sm font-semibold">{inr(product.selling_price)}</p>
                </div>

                {loadingMeta ? (
                    <div className="mt-5 space-y-3">
                        <div className="h-12 skeleton rounded-xl" />
                        <div className="h-12 skeleton rounded-xl" />
                    </div>
                ) : (
                    <div className="mt-5 space-y-4">
                        <div>
                            <label className="text-xs font-medium uppercase tracking-wide text-zinc-500">Quantity per refill</label>
                            <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white p-1">
                                <button
                                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                                    className="rounded-full p-2 hover:bg-zinc-50"
                                    aria-label="Decrement"
                                    data-testid="subscribe-qty-dec"
                                >
                                    <Minus className="h-4 w-4" />
                                </button>
                                <span className="min-w-[2.5rem] text-center text-sm font-medium" data-testid="subscribe-qty-value">{qty}</span>
                                <button
                                    onClick={() => setQty((q) => Math.min(99, q + 1))}
                                    className="rounded-full p-2 hover:bg-zinc-50"
                                    aria-label="Increment"
                                    data-testid="subscribe-qty-inc"
                                >
                                    <Plus className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-medium uppercase tracking-wide text-zinc-500">Shipping address</label>
                            {addresses.length === 0 ? (
                                <p className="mt-2 rounded-xl border border-amber-100 bg-amber-50 p-3 text-xs text-amber-700">
                                    No address on file.{" "}
                                    <Link to="/account" className="font-semibold underline">Add one</Link> to subscribe.
                                </p>
                            ) : (
                                <select
                                    value={selectedAddr || ""}
                                    onChange={(e) => setSelectedAddr(e.target.value)}
                                    className="input-clean mt-2"
                                    data-testid="subscribe-address-select"
                                >
                                    {addresses.map((a) => (
                                        <option key={a.id} value={a.id}>
                                            {`${a.label} — ${a.city}, ${a.pincode}`}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        {product.prescription_required && (
                            <div>
                                <label className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                                    Prescription <ShieldCheck className="ml-1 inline h-3 w-3 text-brand-700" />
                                </label>
                                {prescriptions.length === 0 ? (
                                    <p className="mt-2 rounded-xl border border-amber-100 bg-amber-50 p-3 text-xs text-amber-700">
                                        No verified prescriptions on file.{" "}
                                        <Link to="/prescriptions/upload" className="font-semibold underline">Upload one</Link> first.
                                    </p>
                                ) : (
                                    <select
                                        value={selectedRx || ""}
                                        onChange={(e) => setSelectedRx(e.target.value)}
                                        className="input-clean mt-2"
                                        data-testid="subscribe-rx-select"
                                    >
                                        {prescriptions.map((rx) => (
                                            <option key={rx.id} value={rx.id}>
                                                {`${rx.doctor_name || "Dr."} — ${rx.prescription_date || "no date"}`}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        )}

                        <div className="rounded-2xl border border-brand-100 bg-brand-50/40 p-4">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-zinc-600">Per-refill total</span>
                                <span className="font-semibold text-zinc-900" data-testid="subscribe-total">{inr(total)}</span>
                            </div>
                            <p className="mt-1 text-[11px] text-zinc-500">
                                You'll only be charged when you confirm each refill. Shipping & taxes shown at refill time.
                            </p>
                        </div>
                    </div>
                )}

                <button
                    onClick={submit}
                    disabled={submitting || loadingMeta || addresses.length === 0 || (product.prescription_required && prescriptions.length === 0)}
                    className="btn-brand mt-6 w-full"
                    data-testid="subscribe-confirm-btn"
                >
                    <Repeat className="h-4 w-4" />
                    {submitting ? "Setting up..." : "Subscribe monthly"}
                </button>
                <p className="mt-3 text-center text-[11px] text-zinc-500">Cancel anytime from your subscriptions page.</p>
            </div>
        </div>
    );
};

export default SubscribeDialog;
